import Paper from '@/components/Paper'
import MonthRangeSelector from '@/components/MonthRangeSelector'
// import { CHART_COLORS } from '@/constants/chartColors' // Reserved for future chart customization
import { SCHEMES } from '@/components/EchartCustomized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { formatCurrency } from '@/utils/function'
import dayjs from 'dayjs'
import { Calendar } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { Button, Typogaphy } from 'vnetwork-ui'
import { getMonthlyServiceBreakdown } from '../../../services'
import { SalesTargetFilters } from '../../../types'
import ServiceBreakdownAIInsights from './AIInsights'
import MonthlyTable from './MonthlyTable'
import { PDFExportButton } from '../ExportButtons'

interface MonthlyServiceBreakdownProps {
    filters: SalesTargetFilters
}

const COLORS = SCHEMES.default

const MonthlyServiceBreakdown = ({ filters }: MonthlyServiceBreakdownProps) => {
    const { t } = useTranslation()
    const chartRef = useRef<HTMLDivElement>(null)
    const [selectedServices, setSelectedServices] = useState<
        Record<string, boolean>
    >({})
    const [compareRange, setCompareRange] = useState<{
        gte: string | null
        lte: string | null
    }>({ gte: null, lte: null })
    const [showCompareSelector, setShowCompareSelector] = useState(false)

    const hasComparison = !!compareRange.gte && !!compareRange.lte

    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BUSINESS_TARGETS,
            'monthly-service-breakdown',
            filters.gte,
            filters.lte,
            filters?.employee_id ?? [],
        ],
        queryFn: () =>
            getMonthlyServiceBreakdown({
                gte: filters.gte,
                lte: filters.lte,
                employee_id: filters?.employee_id ?? [],
            }),
        enabled: !!filters.gte && !!filters.lte,
    })

    // Comparison data
    const { data: comparisonData } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BUSINESS_TARGETS,
            'monthly-service-breakdown-comparison',
            compareRange.gte,
            compareRange.lte,
            filters?.employee_id ?? [],
        ],
        queryFn: () =>
            getMonthlyServiceBreakdown({
                gte: compareRange.gte!,
                lte: compareRange.lte!,
                employee_id: filters?.employee_id ?? [],
            }),
        enabled: hasComparison,
    })

    const serviceLines = useMemo(() => {
        if (!data || !Array.isArray(data) || data.length === 0) return []

        const sortedServices = [...data].sort(
            (a: any, b: any) => b.total_revenue - a.total_revenue
        )

        return sortedServices.map((service: any, index: number) => ({
            key: service.product_group_name,
            name: service.product_group_name,
            color: COLORS[index % COLORS.length],
        }))
    }, [data])

    useMemo(() => {
        if (serviceLines.length > 0) {
            const initial: Record<string, boolean> = {}
            serviceLines.forEach((line) => {
                if (!(line.key in selectedServices)) {
                    initial[line.key] = true
                }
            })
            if (Object.keys(initial).length > 0) {
                setSelectedServices((prev) => ({ ...prev, ...initial }))
            }
        }
    }, [serviceLines])

    // Merged chart data with both current and comparison
    const chartData = useMemo(() => {
        if (!data || !Array.isArray(data) || data.length === 0) return []

        const sortedServices = [...data].sort(
            (a: any, b: any) => b.total_revenue - a.total_revenue
        )

        // Get months from current data
        const currentMonthsSet = new Set<string>()
        data.forEach((service: any) => {
            service.monthly_breakdown?.forEach((month: any) => {
                currentMonthsSet.add(month.revenue_month)
            })
        })
        const currentMonths = Array.from(currentMonthsSet).sort()

        // Get months from comparison data
        const compMonthsSet = new Set<string>()
        if (comparisonData && Array.isArray(comparisonData)) {
            comparisonData.forEach((service: any) => {
                service.monthly_breakdown?.forEach((month: any) => {
                    compMonthsSet.add(month.revenue_month)
                })
            })
        }
        const compMonths = Array.from(compMonthsSet).sort()

        // Use the longer array length
        const maxLength = Math.max(currentMonths.length, compMonths.length)

        return Array.from({ length: maxLength }, (_, index) => {
            const currentMonth = currentMonths[index]
            const compMonth = compMonths[index]

            // Use index as label (1, 2, 3...) to align both periods
            const dataPoint: any = {
                month: currentMonth
                    ? dayjs(currentMonth).format('MM/YYYY')
                    : compMonth
                      ? dayjs(compMonth).format('MM/YYYY')
                      : `${index + 1}`,
            }

            // Add current period data
            if (currentMonth) {
                sortedServices.forEach((service: any) => {
                    const monthData = service.monthly_breakdown?.find(
                        (m: any) => m.revenue_month === currentMonth
                    )
                    if (monthData) {
                        dataPoint[service.product_group_name] =
                            monthData.total_revenue || 0
                    }
                })
            }

            // Add comparison period data with _compare suffix
            if (compMonth && comparisonData) {
                comparisonData.forEach((service: any) => {
                    const monthData = service.monthly_breakdown?.find(
                        (m: any) => m.revenue_month === compMonth
                    )
                    if (monthData) {
                        dataPoint[`${service.product_group_name}_compare`] =
                            monthData.total_revenue || 0
                    }
                })
            }

            return dataPoint
        })
    }, [data, comparisonData])

    // Service totals for AI insights
    const serviceTotals = useMemo(() => {
        if (!data || !Array.isArray(data)) return []

        return data
            .map((service: any) => {
                const compService = comparisonData?.find(
                    (c: any) =>
                        c.product_group_name === service.product_group_name
                )

                return {
                    name: service.product_group_name,
                    total: service.total_revenue || 0,
                    renewal: service.renewal_revenue || 0,
                    newSale: service.new_sale_revenue || 0,
                    upsell: service.upsell_revenue || 0,
                    compTotal: compService?.total_revenue || 0,
                    compRenewal: compService?.renewal_revenue || 0,
                    compNewSale: compService?.new_sale_revenue || 0,
                    compUpsell: compService?.upsell_revenue || 0,
                }
            })
            .sort((a, b) => b.total - a.total)
    }, [data, comparisonData])

    // Get all unique service names from both current and comparison data
    const allServiceNames = useMemo(() => {
        const namesSet = new Set<string>()

        if (data && Array.isArray(data)) {
            data.forEach((service: any) => {
                namesSet.add(service.product_group_name)
            })
        }

        if (comparisonData && Array.isArray(comparisonData)) {
            comparisonData.forEach((service: any) => {
                namesSet.add(service.product_group_name)
            })
        }

        return Array.from(namesSet)
    }, [data, comparisonData])

    // Monthly breakdown table data
    const monthlyTableData = useMemo(() => {
        if (!data || !Array.isArray(data) || data.length === 0)
            return { months: [], services: [] }

        // Get all months
        const monthsSet = new Set<string>()
        data.forEach((service: any) => {
            service.monthly_breakdown?.forEach((month: any) => {
                monthsSet.add(month.revenue_month)
            })
        })
        const months = Array.from(monthsSet).sort()

        // Build service data with monthly breakdown - include all services
        const services = allServiceNames
            .map((serviceName) => {
                const serviceData = data.find(
                    (s: any) => s.product_group_name === serviceName
                )
                const monthlyData: Record<string, number> = {}

                if (serviceData) {
                    serviceData.monthly_breakdown?.forEach((month: any) => {
                        monthlyData[month.revenue_month] =
                            month.total_revenue || 0
                    })
                }

                return {
                    name: serviceName,
                    total: serviceData?.total_revenue || 0,
                    monthlyData,
                }
            })
            .sort((a, b) => b.total - a.total)

        return { months, services }
    }, [data, allServiceNames])

    // Comparison monthly breakdown table data
    const comparisonMonthlyTableData = useMemo(() => {
        if (
            !comparisonData ||
            !Array.isArray(comparisonData) ||
            comparisonData.length === 0
        )
            return { months: [], services: [] }

        // Get all months
        const monthsSet = new Set<string>()
        comparisonData.forEach((service: any) => {
            service.monthly_breakdown?.forEach((month: any) => {
                monthsSet.add(month.revenue_month)
            })
        })
        const months = Array.from(monthsSet).sort()

        // Build service data with monthly breakdown - include all services
        const services = allServiceNames
            .map((serviceName) => {
                const serviceData = comparisonData.find(
                    (s: any) => s.product_group_name === serviceName
                )
                const monthlyData: Record<string, number> = {}

                if (serviceData) {
                    serviceData.monthly_breakdown?.forEach((month: any) => {
                        monthlyData[month.revenue_month] =
                            month.total_revenue || 0
                    })
                }

                return {
                    name: serviceName,
                    total: serviceData?.total_revenue || 0,
                    monthlyData,
                }
            })
            .sort((a, b) => b.total - a.total)

        return { months, services }
    }, [comparisonData, allServiceNames])

    const formatYAxis = (value: number) => {
        const allValues = chartData.flatMap((d) =>
            serviceLines.map((line) => d[line.key] || 0)
        )
        const maxValue = Math.max(...allValues, 0)

        if (maxValue >= 1_000_000_000) {
            return `${(value / 1_000_000_000).toFixed(1)}B`
        } else if (maxValue >= 1_000_000) {
            return `${(value / 1_000_000).toFixed(1)}M`
        } else {
            return `${(value / 1_000).toFixed(0)}K`
        }
    }

    const handleToggleService = (key: string) => {
        setSelectedServices((prev) => ({
            ...prev,
            [key]: !prev[key],
        }))
    }

    const handleSelectAll = () => {
        const allSelected: Record<string, boolean> = {}
        serviceLines.forEach((line) => {
            allSelected[line.key] = true
        })
        setSelectedServices(allSelected)
    }

    const handleDeselectAll = () => {
        const allDeselected: Record<string, boolean> = {}
        serviceLines.forEach((line) => {
            allDeselected[line.key] = false
        })
        setSelectedServices(allDeselected)
    }

    const handleAddComparison = () => {
        if (filters.gte && filters.lte) {
            const gteDate = dayjs(filters.gte)
            const lteDate = dayjs(filters.lte)
            setCompareRange({
                gte: gteDate.subtract(1, 'year').format('YYYY-MM-DD'),
                lte: lteDate.subtract(1, 'year').format('YYYY-MM-DD'),
            })
            setShowCompareSelector(true)
        }
    }

    const handleRemoveComparison = () => {
        setCompareRange({ gte: null, lte: null })
        setShowCompareSelector(false)
    }

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                    <p className="font-medium mb-2">{label}</p>
                    {payload.map((entry: any, index: number) => (
                        <p
                            key={index}
                            className="text-sm"
                            style={{ color: entry.color }}
                        >
                            {entry.name}: {formatCurrency(entry.value)}
                        </p>
                    ))}
                </div>
            )
        }
        return null
    }

    // Format period labels
    const currentPeriodLabel = useMemo(() => {
        if (!filters.gte || !filters.lte) return ''
        return `${dayjs(filters.gte).format('MM/YYYY')} - ${dayjs(filters.lte).format('MM/YYYY')}`
    }, [filters.gte, filters.lte])

    const comparePeriodLabel = useMemo(() => {
        if (!compareRange.gte || !compareRange.lte) return ''
        return `${dayjs(compareRange.gte).format('MM/YYYY')} - ${dayjs(compareRange.lte).format('MM/YYYY')}`
    }, [compareRange.gte, compareRange.lte])

    const renderComponent = renderState()
    if (renderComponent) return renderComponent

    if (serviceLines.length === 0) return null

    return (
        <div className="space-y-2">
            <Paper className="space-y-2">
                <div className="flex items-center justify-between">
                    <Typogaphy size="medium" className="font-medium">
                        {t('employees.performance.monthly_service_breakdown')}
                    </Typogaphy>

                    {/* Comparison selector */}
                    <div className="flex items-center gap-2">
                        {!showCompareSelector ? (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleAddComparison}
                            >
                                <Calendar className="w-4 h-4 mr-1" />
                                {t('common.compare')}
                            </Button>
                        ) : (
                            <div className="flex items-center gap-2">
                                <MonthRangeSelector
                                    value={
                                        compareRange.gte && compareRange.lte
                                            ? {
                                                  gte: compareRange.gte,
                                                  lte: compareRange.lte,
                                              }
                                            : undefined
                                    }
                                    onChange={(value) =>
                                        setCompareRange({
                                            gte: value?.gte || null,
                                            lte: value?.lte || null,
                                        })
                                    }
                                />
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={handleRemoveComparison}
                                    className="!text-red-500"
                                >
                                    {t('common.cancel')}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-md border border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">
                            {t('employees.performance.select_services')}:
                        </span>
                        <div className="flex gap-2">
                            <button
                                onClick={handleSelectAll}
                                className="text-xs px-3 py-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded font-medium"
                            >
                                {t('employees.performance.select_all')}
                            </button>
                            <button
                                onClick={handleDeselectAll}
                                className="text-xs px-3 py-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded font-medium"
                            >
                                {t('employees.performance.deselect_all')}
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {serviceLines.map((line: any) => (
                            <label
                                key={line.key}
                                className="flex items-start gap-2 cursor-pointer hover:bg-white px-2 py-1.5 rounded transition-colors"
                            >
                                <input
                                    type="checkbox"
                                    checked={
                                        selectedServices[line.key] ?? false
                                    }
                                    onChange={() =>
                                        handleToggleService(line.key)
                                    }
                                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer mt-0.5"
                                />
                                <div className="flex flex-col gap-1 flex-1 min-w-0">
                                    <span
                                        className="text-xs truncate font-medium"
                                        title={line.name}
                                        style={{ color: line.color }}
                                    >
                                        {line.name}
                                    </span>
                                    {/* Line indicators */}
                                    {hasComparison && (
                                        <div className="flex flex-col gap-0.5">
                                            <div className="flex items-center gap-1">
                                                <div
                                                    className="w-4 h-0.5"
                                                    style={{
                                                        backgroundColor:
                                                            line.color,
                                                    }}
                                                />
                                                <span className="text-[10px] text-gray-500">
                                                    {currentPeriodLabel}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <div
                                                    className="w-4 h-0.5"
                                                    style={{
                                                        backgroundImage: `linear-gradient(to right, ${line.color} 50%, transparent 50%)`,
                                                        backgroundSize:
                                                            '4px 2px',
                                                    }}
                                                />
                                                <span className="text-[10px] text-gray-400">
                                                    {comparePeriodLabel}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </label>
                        ))}
                    </div>
                </div>

                <div ref={chartRef} className="relative">
                    <div className="absolute top-0 right-0">
                        <PDFExportButton
                            chartRef={chartRef}
                            filename="monthly-service-breakdown"
                        />
                    </div>
                    <ResponsiveContainer width="100%" height={350}>
                        <LineChart
                            data={chartData}
                            margin={{
                                top: 5,
                                right: 30,
                                left: 20,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke={SCHEMES.gray}
                            />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                            <YAxis
                                tickFormatter={formatYAxis}
                                tick={{ fontSize: 12 }}
                                width={60}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            {serviceLines
                                .filter((line) => selectedServices[line.key])
                                .map((line: any) => (
                                    <Line
                                        key={line.key}
                                        type="monotone"
                                        dataKey={line.key}
                                        name={line.name}
                                        stroke={line.color}
                                        strokeWidth={2}
                                        dot={{ fill: line.color, r: 3 }}
                                    />
                                ))}
                            {/* Comparison lines */}
                            {hasComparison &&
                                serviceLines
                                    .filter(
                                        (line) => selectedServices[line.key]
                                    )
                                    .map((line: any) => (
                                        <Line
                                            key={`${line.key}-compare`}
                                            type="monotone"
                                            dataKey={`${line.key}_compare`}
                                            name={`${line.name} (${comparePeriodLabel})`}
                                            stroke={line.color}
                                            strokeWidth={2}
                                            strokeDasharray="5 5"
                                            dot={{ fill: line.color, r: 2 }}
                                            opacity={0.6}
                                        />
                                    ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Monthly Breakdown Tables */}
                <div className="mt-4 space-y-4">
                    <MonthlyTable
                        months={monthlyTableData.months}
                        services={monthlyTableData.services}
                        periodLabel={currentPeriodLabel}
                        comparisonServices={
                            hasComparison
                                ? comparisonMonthlyTableData.services
                                : undefined
                        }
                        comparisonMonths={
                            hasComparison
                                ? comparisonMonthlyTableData.months
                                : undefined
                        }
                    />

                    {hasComparison &&
                        comparisonMonthlyTableData.services.length > 0 && (
                            <MonthlyTable
                                months={comparisonMonthlyTableData.months}
                                services={comparisonMonthlyTableData.services}
                                periodLabel={comparePeriodLabel}
                                isComparison
                            />
                        )}
                </div>
            </Paper>

            {/* AI Insights */}
            <ServiceBreakdownAIInsights
                serviceData={serviceTotals}
                comparisonServiceData={
                    hasComparison ? serviceTotals : undefined
                }
                periodRange={currentPeriodLabel}
                comparisonPeriodRange={comparePeriodLabel}
                hasComparison={hasComparison}
            />
        </div>
    )
}

export default MonthlyServiceBreakdown
